
import React from "react";
import "./platform/i18n/i18n";

import Drawer from "./components/ui/Drawer";
import AppMenu from "./components/navigation/AppMenu";
import AppMenuSection from "./components/navigation/AppMenuSection";
import AppMenuAction from "./components/navigation/AppMenuAction";
import { Accordion } from "./components/ui/Accordion";

import { MenuProvider } from "./context/MenuContext";
import { useAuth } from "./context/AuthContext";
import { useTranslation } from "react-i18next";

import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";

import LandingPage from "./pages/LandingPage";
import Login from "./pages/Login";
import Logout from "./pages/Logout";
import Register from "./pages/Register";
import PrivateRoute from "./pages/PrivateRoute";

/* God */
import GodDashboard from "./pages/God/GodDashboard";
import GodCreateVendorAndAdmin from "./pages/God/GodCreateVendorAndAdmin";
import GodUpdateUserInfo from "./pages/God/GodUpdateUserInfo";
import GodUpdateUserProfile from "./pages/God/GodUpdateUserProfile";
import GodResetPassword from "./pages/God/GodResetPassword";

/* Admin */
import AdminBusesReport from "./pages/Admin/AdminBusesReport";
import AdminCreateBus from "./pages/Admin/AdminCreateBus";
import AdminCreateDriver from "./pages/Admin/AdminCreateDriver";
import AdminCreatePayerWallet from "./pages/Admin/AdminCreatePayerWallet";
import AdminCreateRoute from "./pages/Admin/AdminCreateRoute";
import AdminCreateRouteWallet from "./pages/Admin/AdminCreateRouteWallet";
import AdminManageServiceProgram from "./pages/Admin/AdminManageServiceProgram";
import AdminDashboard from "./pages/Admin/AdminDashboard";
import AdminDriversReports from "./pages/Admin/AdminDriversReports";
import AdminEnterPriceQuotes from "./pages/Admin/AdminEnterPriceQuotes";
import AdminLogReport from "./pages/Admin/AdminLogReport";
import AdminPayersReport from "./pages/Admin/AdminPayersReport";
import AdminPendingRequests from "./pages/Admin/AdminPendingRequests";
import AdminRequestWalletTopUp from "./pages/Admin/AdminRequestWalletTopUp";
import AdminUpdateUserInfo from "./pages/Admin/AdminUpdateUserInfo";
import AdminUpdateUserProfile from "./pages/Admin/AdminUpdateUserProfile";
import AdminRoutesReport from "./pages/Admin/AdminRoutesReport";
import AdminWalletsReport from "./pages/Admin/AdminWalletsReport";
import AdminPayersWithLowBalanceReport from "./pages/Admin/AdminPayersWithLowBalanceReport";
import AdminUnHappyPayersReport from "./pages/Admin/AdminUnHappyPayersReport";

/* Driver */
import DriverDashboard from "./pages/Driver/DriverDashboard";
import DriverForceRequestPayment from "./pages/Driver/DriverForceRequestPayment";
import DriverAddOfflineTransactions from "./pages/Driver/DriverAddOfflineTransactions";
import DriverToFromPayersReport from "./pages/Driver/DriverToFromPayersReport";
import DriverFromPayersReport from "./pages/Driver/DriverFromPayersReport";

/* Payer */
import PayerDashboard from "./pages/Payer/PayerDashboard";
import PayerInvoiceReport from "./pages/Payer/PayerInvoiceReport";
import PayerPayForTrip from "./pages/Payer/PayerPayForTrip";
import PayerPurchasesReport from "./pages/Payer/PayerPurchasesReport";
import PayerRequestRefund from "./pages/Payer/PayerRequestRefund";
import PayerRequestWalletTopUp from "./pages/Payer/PayerRequestWalletTopUp";
import PayerToFromVendorsReport from "./pages/Payer/PayerToFromVendorsReport";

/* User */
import UserCollectOnBus from "./pages/User/UserCollectOnBus";
import UserRefundPayer from "./pages/User/UserRefundPayer";
import UserTicketCountReport from "./pages/User/UserTicketCountReport";
import UserBatchCollectOnBus from "./pages/User/UserBatchCollectOnBus";
import UserChangePassword from "./pages/User/UserChangePassword";
import UserLogReport from "./pages/User/UserLogReport";
import UserPendingRequestReport from "./pages/User/UserPendingRequestReport";
import UserProfile from "./pages/User/UserProfile";
import UserTrafficReport from "./pages/User/UserTrafficReport";
import UserWhereIsMyBus from "./pages/User/UserWhereIsMyBus";

const AUTH_PAGES = ["/", "/landing", "/login", "/register", "/logout"];

function inferHomePath(user) {
  if (!user) return "/login";

  const role = String(user?.role || "").toLowerCase();

  if (role === "god") return "/god";
  if (role === "admin") return "/admin";
  if (role === "driver") return "/driver";
  if (role === "payer") return "/payer";

  return "/login";
}

function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, loadingUser } = useAuth();
  const { t, i18n } = useTranslation();

  const [menuOpen, setMenuOpen] = React.useState(false);

  const touchStartX = React.useRef(null);
  const touchCurrentX = React.useRef(null);

  const handleTouchStart = (e) => {
    const x = e.touches[0].clientX;

    if (x < 30) {
      touchStartX.current = x;
    }
  };

  const handleTouchMove = (e) => {
    if (touchStartX.current === null) return;

    touchCurrentX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current === null) return;

    const delta =
      touchCurrentX.current === null
        ? 0
        : touchCurrentX.current - touchStartX.current;

    if (delta > 80) {
      setMenuOpen(true);
    }

    touchStartX.current = null;
    touchCurrentX.current = null;
  };

  React.useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const isAuthPage = AUTH_PAGES.includes(location.pathname);
  const isLoggedIn = Boolean(user);
  const role = String(user?.role || "").toLowerCase();

  const isGod = role === "god";
  const isAdmin = role === "admin";
  const isPayer = role === "payer";
  const isDriver = role === "driver";

  const goTo = (path) => {
    navigate(path);
    setMenuOpen(false);
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem("lang", lng);
    setMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    setMenuOpen(false);
    navigate("/login", { replace: true });
  };

  return (
    <MenuProvider
      value={{
        openMenu: () => setMenuOpen(true),
        closeMenu: () => setMenuOpen(false),
      }}
    >
      {!loadingUser && !isAuthPage && isLoggedIn && (
        <Drawer open={menuOpen} onClose={() => setMenuOpen(false)}>
          <AppMenu>
            {isGod && (
              <Accordion mode="single" defaultValue="actions">

                <AppMenuSection value="actions" title={t("app.menu.sections.actions")}>
                  <AppMenuAction onClick={() => goTo("/god/create-vendor-and-admin")}>{t("app.menu.god.actions.create_vendor_and_admin")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/god/update-user-info")}>{t("app.menu.god.actions.update_user_info")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/god/update-user-profile")}>{t("app.menu.god.actions.update_user_profile")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/god/reset-password")}>{t("app.menu.god.actions.reset_user_password")}</AppMenuAction>
                </AppMenuSection>

                <AppMenuSection value="reports" title={t("app.menu.sections.reports")}>
                  <AppMenuAction onClick={() => goTo("/god")}>{t("app.menu.common.summary_dashboard")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/god/create-vendor-and-admin")}>{t("app.menu.god.reports.placeholder")}</AppMenuAction>
                </AppMenuSection>

                <AppMenuSection value="profile" title={t("app.menu.sections.profile")}>
                  <AppMenuAction onClick={() => goTo("/god")}>{t("app.menu.common.summary_dashboard")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/god/profile")}>{t("app.menu.common.my_profile")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/god/change-password")}>{t("app.menu.common.change_password")}</AppMenuAction>
                  <AppMenuAction onClick={() => changeLanguage("en")}>{t("app.menu.common.en")}</AppMenuAction>
                  <AppMenuAction onClick={() => changeLanguage("tr")}>{t("app.menu.common.tr")}</AppMenuAction>
                  <AppMenuAction variant="danger" onClick={handleLogout}>{t("app.menu.common.logout")}</AppMenuAction>
                </AppMenuSection>
              
              </Accordion>
            )}

            {isAdmin && (
              <Accordion mode="single" defaultValue="actions">

                <AppMenuSection value="actions" title={t("app.menu.sections.actions")}>

                  <AppMenuAction onClick={() => goTo("/admin/collect-on-bus")}>{t("app.menu.driver.collect_on_bus")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/admin/batchcollect-on-bus")}>{t("app.menu.driver.batch_collect_on_bus")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/admin/refund-payer")}>{t("app.menu.driver.refund_payer")}</AppMenuAction>

                  <AppMenuAction onClick={() => goTo("/admin/create-bus")}>{t("app.menu.admin.actions.create_bus")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/admin/create-driver")}>{t("app.menu.admin.actions.create_driver")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/admin/create-route")}>{t("app.menu.admin.actions.create_route")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/admin/create-route-wallet")}>{t("app.menu.admin.actions.create_route_wallet")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/admin/create-payer-wallet")}>{t("app.menu.admin.actions.create_payer_wallet")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/admin/enter-price-quote")}>{t("app.menu.admin.actions.enter_price_quote")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/admin/manage-service-program")}>{t("app.menu.admin.actions.manage_service_program")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/admin/request-topup")}>{t("app.menu.admin.actions.request_wallet_topup")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/admin/pending-requests")}>{t("app.menu.admin.reports.pending_requests")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/admin/update-user-info")}>{t("app.menu.admin.actions.update_user_info")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/admin/update-user-profile")}>{t("app.menu.admin.actions.update_user_profile")}</AppMenuAction>
                </AppMenuSection>

                <AppMenuSection value="reports" title={t("app.menu.sections.reports")}>
                  <AppMenuAction onClick={() => goTo("/admin")}>{t("app.menu.common.summary_dashboard")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/admin/payers-with-low-balance")}>{t("app.menu.admin.reports.payers_with_low_balance")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/admin/payers-who-are-not-happy")}>{t("app.menu.admin.reports.payers_who_are_not_happy")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/admin/payers")}>{t("app.menu.admin.reports.payers")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/admin/detailed-logs")}>{t("app.menu.admin.reports.logs")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/admin/wallets")}>{t("app.menu.admin.reports.wallets")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/admin/buses")}>{t("app.menu.admin.reports.buses")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/admin/drivers")}>{t("app.menu.admin.reports.drivers")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/admin/routes")}>{t("app.menu.admin.reports.routes")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/admin/traffic-in-time")}>{t("app.menu.admin.reports.traffic")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/admin/where-is-my-bus")}>{t("app.menu.common.where_is_my_bus")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/admin/ticket-meter-in-time")}>{t("app.menu.driver.ticket_count")}</AppMenuAction>



                </AppMenuSection>

                <AppMenuSection value="profile" title={t("app.menu.sections.profile")}>
                  <AppMenuAction onClick={() => goTo("/admin/profile")}>{t("app.menu.common.my_profile")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/admin/legal-documents")}>{t("app.menu.common.legal_documents")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/admin/change-password")}>{t("app.menu.common.change_password")}</AppMenuAction>
                  <AppMenuAction onClick={() => changeLanguage("en")}>{t("app.menu.common.en")}</AppMenuAction>
                  <AppMenuAction onClick={() => changeLanguage("tr")}>{t("app.menu.common.tr")}</AppMenuAction>
                  <AppMenuAction variant="danger" onClick={handleLogout}>{t("app.menu.common.logout")}</AppMenuAction>
                </AppMenuSection>

              </Accordion>

)}

            {isPayer && (
              <Accordion mode="single" defaultValue="actions">

                <AppMenuSection value="actions" title={t("app.menu.sections.actions")}>
                  <AppMenuAction onClick={() => goTo("/payer/pay-for-trip")}>{t("app.menu.payer.pay_for_trip")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/payer/request-topup")}>{t("app.menu.payer.inform_money_transfer")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/payer/request-ticket-refund")}>{t("app.menu.payer.request_refund")}</AppMenuAction>
                </AppMenuSection>

                <AppMenuSection value="reports" title={t("app.menu.sections.reports")}>
                  <AppMenuAction onClick={() => goTo("/payer")}>{t("app.menu.common.summary_dashboard")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/payer/where-is-my-bus")}>{t("app.menu.common.where_is_my_bus")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/payer/to-from-vendors-in-time")}>{t("app.menu.payer.ticket_history")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/payer/purchases-in-time")}>{t("app.menu.payer.payment_history")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/payer/invoices-in-time")}>{t("app.menu.payer.invoices")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/payer/traffic-in-time")}>{t("app.menu.payer.traffic")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/payer/pending-requests")}>{t("app.menu.payer.pending_requests")}</AppMenuAction>
                </AppMenuSection>

                <AppMenuSection value="profile" title={t("app.menu.sections.profile")}>
                  <AppMenuAction onClick={() => goTo("/payer/profile")}>{t("app.menu.common.my_profile")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/payer/legal-documents")}>{t("app.menu.common.legal_documents")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/payer/change-password")}>{t("app.menu.common.change_password")}</AppMenuAction>
                  <AppMenuAction onClick={() => changeLanguage("en")}>{t("app.menu.common.en")}</AppMenuAction>
                  <AppMenuAction onClick={() => changeLanguage("tr")}>{t("app.menu.common.tr")}</AppMenuAction>
                  <AppMenuAction variant="danger" onClick={handleLogout}>{t("app.menu.common.logout")}</AppMenuAction>
                </AppMenuSection>
              </Accordion>
            )}

            {isDriver && (
              <Accordion mode="single" defaultValue="actions">
                <AppMenuSection value="actions" title={t("app.menu.sections.actions")}>
                  <AppMenuAction onClick={() => goTo("/driver/collect-on-bus")}>{t("app.menu.driver.collect_on_bus")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/driver/batchcollect-on-bus")}>{t("app.menu.driver.batch_collect_on_bus")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/driver/refund-payer")}>{t("app.menu.driver.refund_payer")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/driver/force-request-payment")}>{t("app.menu.driver.force_request_payment")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/driver/add-offline-transactions")}>{t("app.menu.driver.add_offline_transactions")}</AppMenuAction>
                </AppMenuSection>

                <AppMenuSection value="reports" title={t("app.menu.sections.reports")}>
                  <AppMenuAction onClick={() => goTo("/driver")}>{t("app.menu.common.summary_dashboard")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/driver/where-is-my-bus")}>{t("app.menu.common.where_is_my_bus")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/driver/ticket-meter-in-time")}>{t("app.menu.driver.ticket_count")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/driver/from-payers-in-time")}>{t("app.menu.driver.from_payers")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/driver/to-from-payers-in-time")}>{t("app.menu.driver.to_from_payers")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/driver/traffic-in-time")}>{t("app.menu.driver.traffic")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/driver/pending-requests")}>{t("app.menu.driver.pending_requests")}</AppMenuAction>
                </AppMenuSection>

                <AppMenuSection value="profile" title={t("app.menu.sections.profile")}>
                  <AppMenuAction onClick={() => goTo("/driver/profile")}>{t("app.menu.common.my_profile")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/driver/legal-documents")}>{t("app.menu.common.legal_documents")}</AppMenuAction>
                  <AppMenuAction onClick={() => goTo("/driver/change-password")}>{t("app.menu.common.change_password")}</AppMenuAction>
                  <AppMenuAction onClick={() => changeLanguage("en")}>{t("app.menu.common.en")}</AppMenuAction>
                  <AppMenuAction onClick={() => changeLanguage("tr")}>{t("app.menu.common.tr")}</AppMenuAction>
                  <AppMenuAction variant="danger" onClick={handleLogout}>{t("app.menu.common.logout")}</AppMenuAction>
                </AppMenuSection>

              </Accordion>
            )}
          </AppMenu>
        </Drawer>
      )}

      <div
        style={{ padding: "1rem" }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {children}
      </div>
    </MenuProvider>
  );
}

function AppRoutes() {
  const { user, loadingUser } = useAuth();

  if (loadingUser) return null;

  const homePath = inferHomePath(user);

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/landing" element={<LandingPage />} />
        <Route path="/login" element={user ? <Navigate to={homePath} replace /> : <Login />} />
        <Route path="/register" element={user ? <Navigate to={homePath} replace /> : <Register />} />
        <Route path="/logout" element={<Logout />} />

        <Route path="/god" element={<PrivateRoute allowedRoles={["god"]}><GodDashboard /></PrivateRoute>} />
        <Route path="/god/create-vendor-and-admin" element={<PrivateRoute allowedRoles={["god"]}><GodCreateVendorAndAdmin /></PrivateRoute>} />
        <Route path="/god/reset-password" element={<PrivateRoute allowedRoles={["god"]}><GodResetPassword /></PrivateRoute>} />
        <Route path="/god/update-user-info" element={<PrivateRoute allowedRoles={["god"]}><GodUpdateUserInfo /></PrivateRoute>} />
        <Route path="/god/update-user-profile" element={<PrivateRoute allowedRoles={["god"]}><GodUpdateUserProfile /></PrivateRoute>} />
        <Route path="/god/profile" element={<PrivateRoute allowedRoles={["god"]}><UserProfile /></PrivateRoute>} />
        <Route path="/god/legal-documents" element={<PrivateRoute allowedRoles={["god"]}><UserProfile /></PrivateRoute>} />
        <Route path="/god/change-password" element={<PrivateRoute allowedRoles={["god"]}><UserChangePassword /></PrivateRoute>} />

        <Route path="/admin" element={<PrivateRoute allowedRoles={["admin"]}><AdminDashboard /></PrivateRoute>} />

        <Route path="/admin/collect-on-bus" element={<PrivateRoute allowedRoles={["admin"]}><UserCollectOnBus /></PrivateRoute>} />
        <Route path="/admin/batchcollect-on-bus" element={<PrivateRoute allowedRoles={["admin"]}><UserBatchCollectOnBus /></PrivateRoute>} />
        <Route path="/admin/refund-payer" element={<PrivateRoute allowedRoles={["admin"]}><UserRefundPayer /></PrivateRoute>} />

        <Route path="/admin/create-bus" element={<PrivateRoute allowedRoles={["admin"]}><AdminCreateBus /></PrivateRoute>} />
        <Route path="/admin/create-route" element={<PrivateRoute allowedRoles={["admin"]}><AdminCreateRoute /></PrivateRoute>} />
        <Route path="/admin/create-driver" element={<PrivateRoute allowedRoles={["admin"]}><AdminCreateDriver /></PrivateRoute>} />
        <Route path="/admin/create-route-wallet" element={<PrivateRoute allowedRoles={["admin"]}><AdminCreateRouteWallet /></PrivateRoute>} />
        <Route path="/admin/manage-service-program" element={<PrivateRoute allowedRoles={["admin"]}><AdminManageServiceProgram /></PrivateRoute>} />
        <Route path="/admin/create-payer-wallet" element={<PrivateRoute allowedRoles={["admin"]}><AdminCreatePayerWallet /></PrivateRoute>} />
        <Route path="/admin/enter-price-quote" element={<PrivateRoute allowedRoles={["admin"]}><AdminEnterPriceQuotes /></PrivateRoute>} />
        <Route path="/admin/request-topup" element={<PrivateRoute allowedRoles={["admin"]}><AdminRequestWalletTopUp /></PrivateRoute>} />
        <Route path="/admin/pending-requests" element={<PrivateRoute allowedRoles={["admin"]}><AdminPendingRequests /></PrivateRoute>} />
        <Route path="/admin/update-user-info" element={<PrivateRoute allowedRoles={["admin"]}><AdminUpdateUserInfo /></PrivateRoute>} />
        <Route path="/admin/update-user-profile" element={<PrivateRoute allowedRoles={["admin"]}><AdminUpdateUserProfile /></PrivateRoute>} />
        <Route path="/admin/update-user-vendor-profile" element={<Navigate to="/admin/update-user-profile" replace />} />
        <Route path="/admin/buses" element={<PrivateRoute allowedRoles={["admin"]}><AdminBusesReport /></PrivateRoute>} />
        <Route path="/admin/drivers" element={<PrivateRoute allowedRoles={["admin"]}><AdminDriversReports /></PrivateRoute>} />
        <Route path="/admin/routes" element={<PrivateRoute allowedRoles={["admin"]}><AdminRoutesReport /></PrivateRoute>} />
        <Route path="/admin/payers" element={<PrivateRoute allowedRoles={["admin"]}><AdminPayersReport /></PrivateRoute>} />
        <Route path="/admin/detailed-logs" element={<PrivateRoute allowedRoles={["admin"]}><AdminLogReport /></PrivateRoute>} />
        <Route path="/admin/payers-with-low-balance" element={<PrivateRoute allowedRoles={["admin"]}><AdminPayersWithLowBalanceReport /></PrivateRoute>} />
        <Route path="/admin/payers-who-are-not-happy" element={<PrivateRoute allowedRoles={["admin"]}><AdminUnHappyPayersReport /></PrivateRoute>} />        
        <Route path="/admin/wallets" element={<PrivateRoute allowedRoles={["admin"]}><AdminWalletsReport /></PrivateRoute>} />
        <Route path="/admin/traffic-in-time" element={<PrivateRoute allowedRoles={["admin"]}><UserTrafficReport /></PrivateRoute>} />
        <Route path="/admin/ticket-meter-in-time" element={<PrivateRoute allowedRoles={["admin"]}><UserTicketCountReport /></PrivateRoute>} />
        <Route path="/admin/where-is-my-bus" element={<PrivateRoute allowedRoles={["admin"]}><UserWhereIsMyBus /></PrivateRoute>} />


        <Route path="/admin/profile" element={<PrivateRoute allowedRoles={["admin"]}><UserProfile /></PrivateRoute>} />
        <Route path="/admin/legal-documents" element={<PrivateRoute allowedRoles={["admin"]}><UserProfile /></PrivateRoute>} />
        <Route path="/admin/change-password" element={<PrivateRoute allowedRoles={["admin"]}><UserChangePassword /></PrivateRoute>} />

        <Route path="/payer" element={<PrivateRoute allowedRoles={["payer"]}><PayerDashboard /></PrivateRoute>} />
        <Route path="/payer/request-topup" element={<PrivateRoute allowedRoles={["payer"]}><PayerRequestWalletTopUp /></PrivateRoute>} />
        <Route path="/payer/pay-for-trip" element={<PrivateRoute allowedRoles={["payer"]}><PayerPayForTrip /></PrivateRoute>} />
        <Route path="/payer/request-ticket-refund" element={<PrivateRoute allowedRoles={["payer"]}><PayerRequestRefund /></PrivateRoute>} />
        <Route path="/payer/to-from-vendors-in-time" element={<PrivateRoute allowedRoles={["payer"]}><PayerToFromVendorsReport /></PrivateRoute>} />
        <Route path="/payer/purchases-in-time" element={<PrivateRoute allowedRoles={["payer"]}><PayerPurchasesReport /></PrivateRoute>} />
        <Route path="/payer/invoices-in-time" element={<PrivateRoute allowedRoles={["payer"]}><PayerInvoiceReport /></PrivateRoute>} />
        <Route path="/payer/detailed-logs" element={<PrivateRoute allowedRoles={["payer"]}><UserLogReport /></PrivateRoute>} />
        <Route path="/payer/pending-requests" element={<PrivateRoute allowedRoles={["payer"]}><UserPendingRequestReport /></PrivateRoute>} />
        <Route path="/payer/traffic-in-time" element={<PrivateRoute allowedRoles={["payer"]}><UserTrafficReport /></PrivateRoute>} />
        <Route path="/payer/where-is-my-bus" element={<PrivateRoute allowedRoles={["payer"]}><UserWhereIsMyBus /></PrivateRoute>} />
        <Route path="/payer/profile" element={<PrivateRoute allowedRoles={["payer"]}><UserProfile /></PrivateRoute>} />
        <Route path="/payer/legal-documents" element={<PrivateRoute allowedRoles={["payer"]}><UserProfile /></PrivateRoute>} />
        <Route path="/payer/change-password" element={<PrivateRoute allowedRoles={["payer"]}><UserChangePassword /></PrivateRoute>} />

        <Route path="/driver" element={<PrivateRoute allowedRoles={["driver"]}><DriverDashboard /></PrivateRoute>} />
        <Route path="/driver/refund-payer" element={<PrivateRoute allowedRoles={["driver"]}><UserRefundPayer /></PrivateRoute>} />
        <Route path="/driver/force-request-payment" element={<PrivateRoute allowedRoles={["driver"]}><DriverForceRequestPayment /></PrivateRoute>} />
        <Route path="/driver/collect-on-bus" element={<PrivateRoute allowedRoles={["driver"]}><UserCollectOnBus /></PrivateRoute>} />
        <Route path="/driver/batchcollect-on-bus" element={<PrivateRoute allowedRoles={["driver"]}><UserBatchCollectOnBus /></PrivateRoute>} />
        <Route path="/driver/add-offline-transactions" element={<PrivateRoute allowedRoles={["driver"]}><DriverAddOfflineTransactions /></PrivateRoute>} />
        <Route path="/driver/to-from-payers-in-time" element={<PrivateRoute allowedRoles={["driver"]}><DriverToFromPayersReport /></PrivateRoute>} />
        <Route path="/driver/from-payers-in-time" element={<PrivateRoute allowedRoles={["driver"]}><DriverFromPayersReport /></PrivateRoute>} />
        <Route path="/driver/ticket-meter-in-time" element={<PrivateRoute allowedRoles={["driver"]}><UserTicketCountReport /></PrivateRoute>} />
        <Route path="/driver/detailed-logs" element={<PrivateRoute allowedRoles={["driver"]}><UserLogReport /></PrivateRoute>} />
        <Route path="/driver/pending-requests" element={<PrivateRoute allowedRoles={["driver"]}><UserPendingRequestReport /></PrivateRoute>} />
        <Route path="/driver/traffic-in-time" element={<PrivateRoute allowedRoles={["driver"]}><UserTrafficReport /></PrivateRoute>} />
        <Route path="/driver/where-is-my-bus" element={<PrivateRoute allowedRoles={["driver"]}><UserWhereIsMyBus /></PrivateRoute>} />
        <Route path="/driver/profile" element={<PrivateRoute allowedRoles={["driver"]}><UserProfile /></PrivateRoute>} />
        <Route path="/driver/legal-documents" element={<PrivateRoute allowedRoles={["driver"]}><UserProfile /></PrivateRoute>} />
        <Route path="/driver/change-password" element={<PrivateRoute allowedRoles={["driver"]}><UserChangePassword /></PrivateRoute>} />

        <Route path="*" element={<Navigate to={homePath} replace />} />
      </Routes>
    </Layout>
  );
}

function App() {
  return (
    <Router>
      <AppRoutes />
    </Router>
  );
}

export default App;
